import { CHARACTER_ACTIONS } from "@/character/actions";

const GRID_ROW_COUNT = CHARACTER_ACTIONS.length;

export type GridAlign = { x: number; y: number };

/** User overrides for sheet grid alignment and cell size. */
export type GridTuning = {
  /** Force 96×96 cells and template label offset when sheet has 7 columns. */
  lockTemplateGrid: boolean;
  /** Square cell size in px; null = infer from image dimensions. */
  frameSize: number | null;
  offset: GridAlign;
  /** When true, row uses `rowOffsetY[row]` instead of sharing only global offset Y. */
  rowSplit?: boolean[];
  /** Extra Y offset (px) for split rows on the source sheet. */
  rowOffsetY?: number[];
  /** Extra X offset (px) for split rows on the source sheet. */
  rowOffsetX?: number[];
};

export type NormalizedGridTuning = GridTuning & {
  rowSplit: boolean[];
  rowOffsetY: number[];
  rowOffsetX: number[];
};

export function emptyRowSplit(): boolean[] {
  return Array.from({ length: GRID_ROW_COUNT }, () => false);
}

export function emptyRowOffsetY(): number[] {
  return Array.from({ length: GRID_ROW_COUNT }, () => 0);
}

export function emptyRowOffsetX(): number[] {
  return Array.from({ length: GRID_ROW_COUNT }, () => 0);
}

export function normalizeGridTuning(tuning: GridTuning): NormalizedGridTuning {
  const rowSplit = tuning.rowSplit?.slice(0, GRID_ROW_COUNT) ?? emptyRowSplit();
  while (rowSplit.length < GRID_ROW_COUNT) rowSplit.push(false);
  const rowOffsetY = tuning.rowOffsetY?.slice(0, GRID_ROW_COUNT) ?? emptyRowOffsetY();
  while (rowOffsetY.length < GRID_ROW_COUNT) rowOffsetY.push(0);
  const rowOffsetX = tuning.rowOffsetX?.slice(0, GRID_ROW_COUNT) ?? emptyRowOffsetX();
  while (rowOffsetX.length < GRID_ROW_COUNT) rowOffsetX.push(0);
  return { ...tuning, rowSplit, rowOffsetY, rowOffsetX };
}

export function splitRowIndexes(tuning: GridTuning): number[] {
  const { rowSplit } = normalizeGridTuning(tuning);
  return rowSplit.map((split, row) => (split ? row : -1)).filter((row) => row >= 0);
}

export function allRowsSplit(tuning: GridTuning): boolean {
  const { rowSplit } = normalizeGridTuning(tuning);
  return rowSplit.every(Boolean);
}

export function fullRowSplit(): boolean[] {
  return Array.from({ length: GRID_ROW_COUNT }, () => true);
}

export type CharacterDraft = {
  name: string;
  spritesheetDataUrl: string | null;
  gridTuning: GridTuning;
};

export function defaultGridTuning(): GridTuning {
  return {
    lockTemplateGrid: false,
    frameSize: null,
    offset: { x: 0, y: 0 },
    rowSplit: emptyRowSplit(),
    rowOffsetY: emptyRowOffsetY(),
    rowOffsetX: emptyRowOffsetX(),
  };
}

/** Infer lock + pitch from image dimensions (e.g. after upload or reset). */
export function gridTuningForImageSize(
  width: number,
  height: number,
  supportsTemplate: (w: number, h: number) => boolean,
): GridTuning {
  return {
    lockTemplateGrid: supportsTemplate(width, height),
    frameSize: null,
    offset: { x: 0, y: 0 },
    rowSplit: emptyRowSplit(),
    rowOffsetY: emptyRowOffsetY(),
    rowOffsetX: emptyRowOffsetX(),
  };
}

export function createEmptyDraft(): CharacterDraft {
  return { name: "", spritesheetDataUrl: null, gridTuning: defaultGridTuning() };
}

export function isDraftComplete(draft: CharacterDraft): boolean {
  return Boolean(draft.name.trim() && draft.spritesheetDataUrl);
}
